import { useState } from "react";
import { Link } from "react-router-dom";
import {
  CaretLeft, MegaphoneSimple, PaperPlaneTilt, Eye,
  Users, UserCircle, Clock, UsersThree,
  FileText, TextAa, PencilLine,
} from "@phosphor-icons/react";
import apiClient from "../../lib/api";
import AdminSection from "../../components/admin/AdminSection";
import { toast } from "sonner";

const AUDIENCES = [
  { key: "all",       label: "Todos los usuarios",      desc: "Cualquiera registrado (excluye suspendidos)",          icon: UsersThree, color: "amber" },
  { key: "active",    label: "Suscriptores activos",    desc: "Status: active o trialing",                            icon: Users,      color: "emerald" },
  { key: "trialing",  label: "Solo en prueba",          desc: "Usuarios con periodo de prueba activo (trialing)",     icon: Clock,      color: "blue" },
  { key: "inactive",  label: "Inactivos / vencidos",    desc: "Cuentas creadas pero sin suscripción activa",          icon: UserCircle, color: "rose" },
];

const COLOR_MAP = {
  amber:   { bg: "bg-amber-500/10",   border: "border-amber-500/40",   text: "text-amber-300",   ring: "ring-amber-500/40" },
  emerald: { bg: "bg-emerald-500/10", border: "border-emerald-500/40", text: "text-emerald-300", ring: "ring-emerald-500/40" },
  blue:    { bg: "bg-blue-500/10",    border: "border-blue-500/40",    text: "text-blue-300",    ring: "ring-blue-500/40" },
  rose:    { bg: "bg-rose-500/10",    border: "border-rose-500/40",    text: "text-rose-300",    ring: "ring-rose-500/40" },
};

const TEMPLATES = {
  blank: { name: "En blanco", body: "" },
  pick_today: {
    name: "Aviso pick del día",
    body:
      "<p>Acabamos de publicar el <strong>pick del día</strong> en la plataforma.</p>" +
      "<p>El análisis completo y el stake recomendado están en tu dashboard. Si tu suscripción está activa, ya puedes verlo.</p>" +
      "<p><a href=\"https://ganasconronald.com\" style=\"color:#D4AF37;font-weight:700;\">Ir a la plataforma →</a></p>",
  },
  maintenance: {
    name: "Mantenimiento programado",
    body:
      "<p>Queremos avisarte que mañana entre las <strong>2:00 AM y 4:00 AM</strong> (hora UTC) realizaremos una actualización breve de la plataforma.</p>" +
      "<p>Durante ese lapso es posible que algunas funciones queden temporalmente fuera de servicio. Volveremos en cuanto termine la actualización.</p>" +
      "<p>Gracias por tu paciencia.</p>",
  },
  promo_renewal: {
    name: "Recordatorio renovación",
    body:
      "<p>Tu suscripción está por vencer. Renueva ahora para no perder acceso al pick del día ni al chat privado.</p>" +
      "<p><a href=\"https://ganasconronald.com/plans\" style=\"color:#D4AF37;font-weight:700;\">Renovar mi plan →</a></p>",
  },
};

export default function AdminBroadcastPage() {
  const [subject, setSubject]   = useState("");
  const [body, setBody]         = useState("");
  const [audience, setAudience] = useState("all");
  const [sending, setSending]   = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const applyTemplate = (key) => {
    const t = TEMPLATES[key];
    if (t && t.body !== undefined) {
      setBody(t.body);
      toast.success(`Plantilla "${t.name}" aplicada`);
    }
  };

  const sendBroadcast = async (testOnly) => {
    if (!subject.trim()) { toast.error("Falta el asunto"); return; }
    if (!body.trim())    { toast.error("Falta el cuerpo del mensaje"); return; }

    if (!testOnly) {
      const ok = window.confirm(
        `⚠️ Estás por enviar este correo a la audiencia "${audience}".\n\n` +
        `Asunto: ${subject}\n\nEsta acción NO se puede deshacer. ¿Confirmas?`
      );
      if (!ok) return;
    }

    setSending(true);
    setLastResult(null);
    try {
      const r = await apiClient.post("/admin/broadcast-email", {
        subject: subject.trim(),
        body,
        audience,
        test_only: !!testOnly,
      });
      setLastResult(r.data);
      if (testOnly) {
        toast.success("Email de prueba enviado a tu cuenta");
      } else {
        toast.success(`Enviado: ${r.data.sent} de ${r.data.total}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al enviar");
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0B0D] text-zinc-100 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0A0B0D]/95 backdrop-blur border-b border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2 text-zinc-400 hover:text-white transition" data-testid="back-to-admin">
            <CaretLeft className="w-5 h-5" />
            <span className="text-sm">Admin</span>
          </Link>
          <div className="flex items-center gap-2">
            <MegaphoneSimple className="w-5 h-5 text-amber-400" weight="fill" />
            <h1 className="text-base font-bold">Email masivo</h1>
          </div>
          <div className="w-16" />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Intro */}
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.03] p-5">
          <p className="text-sm text-zinc-300 leading-relaxed">
            Escribe un correo y envíalo a un segmento de usuarios. Se envuelve automáticamente en la plantilla de la marca
            (saludo, footer, aviso de juego responsable). El HTML del cuerpo lo controlas tú.
          </p>
        </div>

        {/* Plantillas rápidas */}
        <AdminSection icon={FileText} title="Plantillas rápidas" subtitle="Aplica un texto base con un click" color="amber">
          <div className="flex flex-wrap gap-2">
            {Object.entries(TEMPLATES).map(([key, t]) => (
              <button
                key={key}
                onClick={() => applyTemplate(key)}
                className="px-3.5 py-1.5 rounded-lg text-xs font-semibold border border-zinc-700 text-zinc-300 hover:bg-white/5 hover:border-amber-500/40 hover:text-amber-300 transition"
                data-testid={`template-${key}`}
              >
                {t.name}
              </button>
            ))}
          </div>
        </AdminSection>

        {/* Audiencia */}
        <AdminSection icon={UsersThree} title="Audiencia" subtitle="Elige a quién llega este correo" color="violet">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {AUDIENCES.map(a => {
              const Icon  = a.icon;
              const c     = COLOR_MAP[a.color];
              const sel   = audience === a.key;
              return (
                <button
                  key={a.key}
                  onClick={() => setAudience(a.key)}
                  className={`text-left p-4 rounded-xl border-2 transition flex items-start gap-3 ${sel ? `${c.bg} ${c.border} ring-2 ${c.ring}` : "border-zinc-800 hover:border-zinc-700 bg-zinc-900/30"}`}
                  data-testid={`audience-${a.key}`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${sel ? c.bg : "bg-zinc-800/50"}`}>
                    <Icon className={`w-5 h-5 ${sel ? c.text : "text-zinc-500"}`} weight={sel ? "fill" : "regular"} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-bold ${sel ? "text-white" : "text-zinc-300"}`}>{a.label}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{a.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </AdminSection>

        {/* Asunto */}
        <AdminSection icon={TextAa} title="Asunto" subtitle="Línea de asunto del correo" color="sky">
          <input
            type="text"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="Ej: Mantenimiento programado este domingo"
            maxLength={200}
            className="w-full px-4 py-3 rounded-xl bg-zinc-900/60 border border-zinc-700 text-white text-sm outline-none focus:border-amber-500 transition"
            data-testid="broadcast-subject"
          />
          <div className="text-[10px] text-zinc-600 text-right">{subject.length} / 200</div>
        </AdminSection>

        {/* Body */}
        <AdminSection
          icon={PencilLine}
          title="Cuerpo del mensaje"
          subtitle="HTML permitido: negritas, enlaces, párrafos, listas"
          color="cyan"
          action={
            <button
              onClick={() => setShowPreview(p => !p)}
              className="flex items-center gap-1.5 text-[11px] text-amber-400 hover:text-amber-300 transition"
              data-testid="toggle-preview"
            >
              <Eye className="w-3.5 h-3.5" />
              {showPreview ? "Ocultar preview" : "Ver preview"}
            </button>
          }
        >
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            rows={10}
            placeholder="<p>Escribe tu mensaje aquí. Puedes usar HTML: <strong>, <a>, <p>, <br>, listas, etc.</p>"
            className="w-full px-4 py-3 rounded-xl bg-zinc-900/60 border border-zinc-700 text-white text-sm outline-none focus:border-amber-500 transition font-mono"
            data-testid="broadcast-body"
          />
          {showPreview && (
            <div className="rounded-xl border border-zinc-700 bg-[#0d0d1a] p-5" data-testid="broadcast-preview">
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold mb-3">Vista previa del cuerpo</div>
              <div
                className="prose prose-invert max-w-none text-sm text-zinc-200"
                dangerouslySetInnerHTML={{ __html: body || "<p class='text-zinc-600 italic'>Aquí aparecerá lo que escribas arriba…</p>" }}
              />
              <div className="text-[10px] text-zinc-500 mt-4 pt-3 border-t border-zinc-800">
                Nota: al enviar se añaden automáticamente el saludo (con nombre), franja dorada, footer y unsubscribe.
              </div>
            </div>
          )}
        </AdminSection>

        {/* Action bar */}
        <section className="sticky bottom-0 bg-gradient-to-t from-[#0A0B0D] to-transparent pt-6 pb-2 -mx-4 px-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => sendBroadcast(true)}
              disabled={sending || !subject.trim() || !body.trim()}
              className="flex-1 py-3.5 rounded-xl text-xs font-black uppercase tracking-wider border border-zinc-700 text-zinc-200 hover:bg-white/5 transition disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="broadcast-test-btn"
            >
              📩 Enviar prueba solo a mí
            </button>
            <button
              onClick={() => sendBroadcast(false)}
              disabled={sending || !subject.trim() || !body.trim()}
              className="flex-[2] py-3.5 rounded-xl text-xs font-black uppercase tracking-wider bg-amber-500 hover:bg-amber-400 text-black transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="broadcast-send-btn"
            >
              <PaperPlaneTilt className="w-4 h-4" weight="fill" />
              {sending ? "Enviando…" : `Enviar a "${AUDIENCES.find(a => a.key === audience)?.label}"`}
            </button>
          </div>
        </section>

        {/* Last result */}
        {lastResult && (
          <div
            className={`rounded-xl border p-4 ${lastResult.ok ? "border-emerald-500/40 bg-emerald-500/[0.04]" : "border-rose-500/40 bg-rose-500/[0.04]"}`}
            data-testid="broadcast-result"
          >
            <div className={`text-sm font-bold ${lastResult.ok ? "text-emerald-300" : "text-rose-300"}`}>
              {lastResult.ok ? "✅ Envío completado" : "⚠️ Envío con errores"}
            </div>
            <div className="text-xs text-zinc-400 mt-1.5 space-y-0.5">
              <div>Audiencia: <span className="text-zinc-200">{lastResult.audience}</span></div>
              <div>Total destinatarios: <span className="text-zinc-200">{lastResult.total}</span></div>
              <div>Enviados: <span className="text-emerald-300">{lastResult.sent}</span></div>
              {lastResult.failures > 0 && <div>Fallos: <span className="text-rose-300">{lastResult.failures}</span></div>}
              {lastResult.errors?.length > 0 && (
                <div className="mt-2 text-[10px] text-rose-400/80">
                  {lastResult.errors.map((e, i) => <div key={i}>• {e}</div>)}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
